import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaHistory, FaArrowLeft, FaChevronLeft, FaChevronRight, FaSpinner, FaCheckCircle, FaTimesCircle, FaFileExcel } from 'react-icons/fa';
import { fetchImportHistory } from '../services/utilityService';

const ImportJobSchedulesHistoryPage = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);

  // Cursor stack: cursorStack[pageIndex] is the cursor used to fetch that page.
  // First page uses null cursor. Pushing nextCursor enables forward navigation.
  const [cursorStack, setCursorStack] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);

  const loadPage = useCallback(async (cursor) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchImportHistory(cursor);
      setItems(res.data.items || []);
      setNextCursor(res.data.nextCursor || null);
    } catch (err) {
      setError(err.message || 'Failed to load history.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(cursorStack[pageIndex]);
  }, [pageIndex, cursorStack, loadPage]);

  const handleNext = () => {
    if (!nextCursor) return;
    const newStack = cursorStack.slice(0, pageIndex + 1).concat(nextCursor);
    setCursorStack(newStack);
    setPageIndex(pageIndex + 1);
  };

  const handlePrev = () => {
    if (pageIndex === 0) return;
    setPageIndex(pageIndex - 1);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <FaHistory className="text-orange-500" /> Import History
          </h1>
          <p className="text-sm text-gray-500 mt-1">Previous job schedule imports</p>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/utilities"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 underline"
          >
            <FaArrowLeft className="h-3 w-3" /> Back to Utilities
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-sm text-jims-cyan hover:text-jims-cyan-dark underline"
          >
            <FaFileExcel className="h-3 w-3" /> New Import
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mx-4 sm:mx-0">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
            <FaSpinner className="animate-spin" /> Loading...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">{error}</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <FaHistory className="text-4xl mx-auto mb-3 opacity-30" />
            <p className="font-medium">No import history yet</p>
            <p className="text-sm mt-1">Imported schedules will appear here.</p>
          </div>
        ) : (
          <>
            <table className="min-w-full text-sm divide-y divide-gray-100">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">Date / Time</th>
                  <th className="px-4 py-3 text-left">File</th>
                  <th className="px-4 py-3 text-center">Imported</th>
                  <th className="px-4 py-3 text-center">Failed</th>
                  <th className="px-4 py-3 text-left">Imported By</th>
                  <th className="px-4 py-3 text-left" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, idx) => (
                  <tr
                    key={item.id}
                    className={`cursor-pointer hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    onClick={() => navigate(`/utilities/import-job-schedules/history/${item.id}`)}
                  >
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(item.importedAt)}</td>
                    <td className="px-4 py-3 text-gray-700 max-w-xs">
                      <div className="truncate font-mono text-xs bg-gray-100 rounded px-2 py-1 inline-block max-w-full" title={item.fileName}>
                        {item.fileName}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 text-green-700 font-semibold">
                        <FaCheckCircle className="text-green-500" />
                        {item.summary?.imported ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.summary?.failed > 0 ? (
                        <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                          <FaTimesCircle className="text-red-400" />
                          {item.summary.failed}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{item.importedBy?.email || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/utilities/import-job-schedules/history/${item.id}`}
                        className="text-jims-cyan hover:underline text-xs font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
              <p className="text-sm text-gray-500">Page {pageIndex + 1}</p>
              <div className="flex gap-2">
                <button
                  onClick={handlePrev}
                  disabled={pageIndex === 0}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <FaChevronLeft className="h-3 w-3" /> Prev
                </button>
                <button
                  onClick={handleNext}
                  disabled={!nextCursor}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Next <FaChevronRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportJobSchedulesHistoryPage;
